import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BooleanField, DatagridConfigurable, FunctionField, NumberField, TextField, useDataProvider, useListController, useStore, useUnselectAll } from 'react-admin';
import { Backdrop, Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import { formatLocalTime } from '../../components/ex_dayjs';
import RaList from '../../components/ra-list/RaList';
import RenderControls from '../../components/ra-list/RenderControls';
import { getDefaultUtcOffset, loadSourceType, reloadPage, setDefaultValuesFromURL } from '../../utils/common';
import { ExchangeMarket, MappingOptions, Countries } from '../../utils/enum';
import MappingPopup from './components/MappingPopup';
import SyncButton from './components/SyncButton';
import SyncEventPopup from './components/SyncEventPopup';
import UnmapAllButon from './components/UnmapAllButon';
import UrlFormatter from '../../components/ra-list/formatters/UrlFormatter';
import SkipPricerAllButon from './components/SkipPricerAllButon';
import DateTimeDiffFormatter from '../../components/ra-list/formatters/DateTimeDiffFormatter';
import { ReasonTagsFormat } from './formatters/ReasonTagsFormat';
import MappingReportButton from './components/MappingReportButton';
import { FilterListProvider } from '../../context/FilterListProvider';

import './style.css';
import { OpenQueueUploadButton } from './components/addition-listings/OpenQueueUploadButton';
import { ReviewQueueAction } from './components/addition-listings/ReviewQueueAction';
import { insertBreakPoints } from './formatters/constants';
import type { IData } from '../../types';

const cellColsCss = {
    time: 'listing-mapping-time-cell',
};

const defaultOrderby = 'dateTime asc';
const storeListingKey = 'preferences.ListingsMapping.datagrid';
const HIDE_COLS = ['reasonTags', 'venueId'];

const ListingsMapping = () => {
    const location = useLocation();
    const dataProvider = useDataProvider();
    const refbtnFilter = useRef(null);
    const [rowItem, setRowItem] = React.useState<IData | null>(null);
    const [sortModel, setSortModel] = React.useState([defaultOrderby]);
    const { resource, data, selectedIds } = useListController();
    const unselectAll = useUnselectAll(resource);
    const [omitCols] = useStore(`${storeListingKey}.omit`, HIDE_COLS);
    const [availableColumns] = useStore(`${storeListingKey}.availableColumns`, []);
    const [columns, setColums] = useStore(`${storeListingKey}.columns`, availableColumns.map((x: IData) => x.index));
    // for dialogs
    const [state, setState] = React.useState({
        m_isOpen: false,
        m_isLoading: false,
        m_item: {} as IData,
        m_events: [] as IData[],
        // for sync dialog
        s_isOpen: false,
        s_title: 'DETAIL',
        s_data: {} as IData,
        // for unmap all
        unmap_isLoading: false,
        unmap_isOpen: false,
        unmap_alert: false,
        unmap_sync_isLoading: false,
        // for skip pricer
        skipBtn: 'skip pricer',
        smEventId: '', // For sync button
        openBackdrop: false
    });

    const [selection, setSelection] = React.useState({
        selectedIds: [] as (string | number)[],
        isSelection: false,
        MarketId: 1, // VS
        Mapped: -1, // Any
    });
    const [marketType, setMarketType] = React.useState([]);

    const [defaultValues, setDefaultValues] = useState<IData>({
        MarketId: 1,
        Mapped: -1,
        Score: [2, 5],
        adminonly: false,
        exactDateTime: null,
        utc_offset_eq: getDefaultUtcOffset(),
    });

    const controls = [
        { component: 'number', name: 'TicketId', label: 'Ticket ID' },
        { component: 'number', name: 'SmOriginalID', label: 'Sm EventID' },
        { component: 'select', name: 'Type_eq', label: 'Type', choices: marketType },
        { component: 'text', name: 'SiteId_contains', label: 'Site ID', xs: 1 },
        { component: 'select', name: 'Country', choices: Countries },
        { component: 'text', name: 'Name', label: 'Event', xs: 1.5 },
        { component: 'text', name: 'Venue', xs: 1.5 },
        { component: 'number', name: 'VenueId_eq', label: 'Venue ID', xs: 1 },
        { component: 'daterange', name: 'DateTime', label: 'Date Time' },
        { component: 'numberrange', name: 'Score', label: 'Score', xs: 1, decimal: 3, rangeMin: [0, 5], rangeMax: [0, 5], onChange: ((e: IData) => handleSelection(e)) },
        { component: 'select', name: 'MarketId', required: true, label: 'Exchange market', choices: ExchangeMarket, isEmptyOption: false, onChange: ((e: IData) => handleSelection(e)) },
        { component: 'select', name: 'Mapped', choices: MappingOptions, isEmptyOption: false, onChange: ((e: IData) => handleSelection(e)) },
        { component: 'daterange', name: 'Mapped_Time', label: 'Mapped Time', isUTC: true },
        { component: 'text', name: 'Mapped_By_contains', label: 'By', xs: 1 },
        { component: 'checkbox', name: 'adminonly', label: 'Admin only', onChange: (e: IData) => handleSelection(e) },
        { component: 'checkbox', name: 'exactDateTime', label: 'Exact datetime matches', xs: 1.5, onChange: (e: IData) => handleSelection(e) },
        { component: 'checkbox', name: 'isduplicated', label: 'Duplicated', onChange: (e: IData) => handleDuplicated(e) },
        { component: 'checkbox', name: 'notfound', label: 'Not found' },
        { component: 'checkbox', name: 'matchedElseWhere', label: 'Matched Elsewhere', isHide: selection.Mapped !== 2, onChange: ((e: IData) => handleSelection(e)) },
        { component: 'checkbox', name: 'SkipTag', label: 'Skip Tag', isHide: selection.Mapped !== 2, onChange: ((e: IData) => handleSelection(e)) },
        { component: 'text', name: 'utc_offset_eq', isHide: true }
    ];

    useEffect(() => {
        loadSourceType(dataProvider, (response: IData) => {
            setMarketType(response);
        });
        unselectAll();

        if (location.search) {
            let newDefaultValue = { ...defaultValues };
            setDefaultValuesFromURL(location.search, newDefaultValue, controls);
            setDefaultValues(newDefaultValue);
            setSelection((prev) => ({ ...prev, MarketId: newDefaultValue.MarketId }));
        }
    }, []);

    useEffect(() => {
        if (data?.length > 0) {
            let isEnablePricer = data.every(x => x.isSkipPricer === true);
            setState((prev) => ({ ...prev, skipBtn: isEnablePricer ? 'enable pricer' : 'skip pricer' }));
        }
    }, [data]);

    const onSelection = () => {
        // Show/hide checkbox to mapping/skip
        const MARKETS_ALLOW = [1, 2, 4, 13]; // For VS, TN, SG, Go Tickets
        let isSelection = MARKETS_ALLOW.some(x => x === selection.MarketId) && selection.Mapped === 1;
        setSelection((prev) => ({ ...prev, isSelection }));
        // Hide Mapped columns, only show when select Any
        // show reasontags for unmapped
        let hideCols = omitCols;
        if (columns.length) {
            hideCols = availableColumns.filter((x: IData) => !columns.some((y: IData) => y === x.index)).map((x: IData) => x.source);
        }
        if (selection.Mapped === -1) hideCols = HIDE_COLS; // # Any
        else if (selection.Mapped === 2) {
            hideCols = ['isSkipPricer', 'mapped', 'mapped_By', 'mapped_Time', 'reasonTags']; // # Unmatched
            if (selection.matchedElseWhere === true) hideCols.push(...['smEvent', 'smVenue', 'smCity', 'smState', 'smDateTime']); // for matched elsewhere
        } else {
            hideCols = ['mapped'];
            if (selection.Mapped > 0) hideCols.push(...HIDE_COLS); // for <> Unmapped
        }
        let avaiCols = availableColumns.filter((x: IData) => !hideCols.some((y: IData) => y === x.source)).map((x: IData) => x.index);
        setColums(avaiCols);
    };

    const handleDuplicated = (e: IData) => {
        const { value } = e;
        if (value) setSortModel(['smEventID asc', defaultOrderby]);
    };

    /**
     * Allow select multiple checkbox to unmap or not
     * @param {*} name
     * @param {*} value
     * @returns
     */
    function handleSelection (e: IData) {
        const { name, value } = e;
        setSelection((prev) => ({ ...prev, [name]: value }));
        unselectAll();
        // Re-set default values for filters
        switch (name) {
            case 'MarketId':
                setDefaultValues((prev: IData) => ({ ...prev, MarketId: value }));
                break;
            case 'Mapped':
                setDefaultValues((prev: IData) => ({ ...prev, Mapped: value, SkipTag: value === 2 ? true : null, matchedElseWhere: value === 2 ? false : null }));
                break;
            case 'Score_ge':
                setDefaultValues((prev: IData) => ({ ...prev, Score: prev.Score ? [value, prev.Score[1]] : [value, 5] }));
                break;
            case 'Score_le':
                setDefaultValues((prev: IData) => ({ ...prev, Score: prev.Score ? [prev.Score[0], value] : [2, value] }));
                break;
            case 'adminonly':
                setDefaultValues((prev: IData) => ({ ...prev, adminonly: value }));
                break;
            case 'exactDateTime':
                setDefaultValues((prev: IData) => ({ ...prev, exactDateTime: value }));
                break;
            case 'matchedElseWhere':
                setDefaultValues((prev: IData) => ({ ...prev, matchedElseWhere: value }));
                break;
            case 'SkipTag':
                setDefaultValues((prev: IData) => ({ ...prev, SkipTag: value }));
                break;
            default:
                break;
        }
    }

    const handleRowClick = (id: IData, resource: string, record: IData) => {
        const selection = window.getSelection()?.toString();
        if (selection && selection.length > 0) {
            // User is selecting text, do not open dialog
            return;
        }
        setRowItem({ id, resource, record });
    };

    const handleDoubleClick = () => {
        if (rowItem?.record) {
            let _newRecord = { ...rowItem.record, MarketId: selection.MarketId };
            setState((prev) => ({ ...prev, m_isOpen: true, m_item: _newRecord, m_events: [], m_isLoading: true }));
            if (rowItem.id) {
                dataProvider.postData(resource, 'FindEvents', _newRecord)
                    .then((response: IData) => {
                        if (response.data) {
                            setState((prev) => ({ ...prev, m_events: response.data, m_isLoading: false }));
                        }
                    })
                    .catch((error: IData) => console.error(error));
            }
        }
    };

    const handleCloseDialog = (event?: IData | null, dialogName: string | null = null) => {
        let name = event?.currentTarget?.ariaLabel;
        if (!name) name = dialogName;
        if (name) setState((prev) => ({ ...prev, [name]: false }));
    };

    return (
        <Box>
            <RaList
                title={'Event Mapping'}
                sortby={sortModel}
                storeKey={true}
                perPage={50}
                pagination={[50, 100, 200, 500, 1000]}
                filters={
                    <FilterListProvider>
                        <RenderControls
                            exportFile={{
                                columns: ['Type', 'SiteId', 'VenueId', 'Name', 'SmEvent', 'Venue', 'SmVenue', 'City', 'SmCity', 'State', 'SmState', 'DateTime', 'SmDateTime', 'IsSkipPricer',
                                    'Mapped', 'Mapped_By', 'Mapped_Time', 'Score', 'ListingCount', 'Rename_Event', 'Rename_Venue', 'Rename_City', 'Rename_State']
                            }}
                            useHashUrl
                            selectColumn
                            refFilter={refbtnFilter}
                            beforeSearch={onSelection}
                            filterInline={false}
                            actions={
                                <>
                                    <OpenQueueUploadButton />
                                    <SyncButton
                                        dataProvider={dataProvider}
                                        marketId={selection.MarketId}
                                        smEventId={state.smEventId}
                                        setSmEventId={(id: string) => setState((prev) => ({ ...prev, smEventId: id }))}
                                        callback={(resp: IData) => setState((prev) => ({ ...prev, s_isOpen: true, s_data: resp }))} />
                                    <MappingReportButton dataProvider={dataProvider} />
                                </>
                            }
                            controls={controls}
                            defaultValues={defaultValues}
                        />
                    </FilterListProvider>
                }
            >
                <DatagridConfigurable
                    key={new Date().toString()}
                    // optimized
                    omit={omitCols}
                    bulkActionButtons={
                        (() => {
                            if (!selection.isSelection && selection.Mapped !== 2) {
                                return false;
                            }
                            return (
                                <>
                                    {selection.isSelection && (
                                        <>
                                            <UnmapAllButon
                                                dataProvider={dataProvider}
                                                selectedIds={selectedIds}
                                                params={{ resource, data }}
                                                reloadPage={() => {
                                                    unselectAll();
                                                    reloadPage(resource, refbtnFilter);
                                                }} />
                                            <SkipPricerAllButon
                                                dataProvider={dataProvider}
                                                selectedIds={selectedIds}
                                                params={{ resource, data, skipBtn: state.skipBtn }}
                                                reloadPage={() => {
                                                    unselectAll();
                                                    reloadPage(resource, refbtnFilter);
                                                }} />
                                        </>
                                    )}
                                    {selection.Mapped === 2 && (
                                        <ReviewQueueAction
                                            display={true}
                                            dataProvider={dataProvider}
                                            marketId={selection.MarketId}
                                            setState={setState}
                                            reloadPage={() => {
                                                unselectAll();
                                                reloadPage(resource, refbtnFilter);
                                            }}
                                        />
                                    )}
                                </>
                            );
                        })()
                    }
                    onDoubleClick={handleDoubleClick}
                    rowClick={handleRowClick}
                    isRowSelectable={(record) => record.mapped && !record.isUnmap}
                >
                    <TextField source="type" label="Type" />
                    <FunctionField source="siteId" label="SiteId" sx={{ width: '60px', whiteSpace: 'normal' }} render={record => <span>{insertBreakPoints(record.siteId, 7)}</span>} />
                    <TextField source="venueId" label="VenueId" />
                    <UrlFormatter source="name" href="url" />
                    <UrlFormatter label="SmEvent" source="smEvent" href="smUrl" />
                    <FunctionField source="venue" render={record =>
                        <Tooltip title={record.rename_Venue !== '' ? `Renamed from "${record.venue}"` : ''} ><span style={{ color: record.rename_Venue !== '' ? 'orange' : '' }}>{record.rename_Venue || record.venue}</span></Tooltip>}
                    />
                    <TextField source="smVenue" label="SmVenue" />
                    <FunctionField source="city" render={record =>
                        <Tooltip title={record.rename_City !== '' ? `Renamed from "${record.city}"` : ''} ><span style={{ color: record.rename_City !== '' ? 'orange' : '' }}>{record.rename_City || record.city}</span></Tooltip>}
                    />
                    <TextField source="smCity" label="SmCity" />
                    <FunctionField source="state" render={record =>
                        <Tooltip title={record.rename_State !== '' ? `Renamed from "${record.state}"` : ''} ><span style={{ color: record.rename_State !== '' ? 'orange' : '' }}>{record.rename_State || record.state}</span></Tooltip>}
                    />
                    <TextField source="smState" label="SmState" />
                    <FunctionField label="DateTime" source="dateTime" render={record => <DateTimeDiffFormatter source={'dateTime'} record={record} />} />
                    <FunctionField label="SmDateTime" source="smDateTime" render={record => <DateTimeDiffFormatter source={'smDateTime'} record={record} />} />
                    {selection.Mapped === 2 && selection.matchedElseWhere === true &&
                   <FunctionField label="MappedMarket" render={record => {
                       const mappedList = record.otherMarketsInfo?.split('||||') || [];
                       if (!mappedList.length || !record.otherMarketsInfo) return null;

                       return (
                           <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                               {mappedList.map((item: string, index: number) => {
                                   const [_marketId, smEventId, smUrl] = item.split(';;');
                                   return (
                                       <a key={index} href={smUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                           <Chip
                                               label={smEventId}
                                               size="small"
                                               clickable
                                               style={{ cursor: 'pointer' }}
                                           />
                                       </a>
                                   );
                               })}
                           </span>
                       );
                   }} />}
                    <FunctionField label="Skip Pricer" sortBy="isSkipPricer" source="isSkipPricer" render={(record) => <BooleanField source="isSkipPricer" looseValue={record.isSkipPricer === false} />} />
                    <FunctionField
                        label="Mapped"
                        sortBy="mapped"
                        source="mapped"
                        render={(record) => {
                            if (record.mapped > 0) {
                                return <BooleanField source="mapped" record={{ mapped: true }} />;
                            } else if (record.isUnmap === true) {
                                return <BooleanField source="mapped" record={{ mapped: false }} valueLabelFalse="Unmap" style={{ color: 'orange' }} />;
                            } else return null;
                        }}
                    />
                    <ReasonTagsFormat source="reasonTags" />
                    <FunctionField source="mapped_By" label="By" sx={{ width: '60px', whiteSpace: 'normal' }} render={record => <span>{insertBreakPoints(record.mapped_By, 5)}</span>} />
                    <FunctionField label="Time" source="mapped_Time" cellClassName={cellColsCss.time} render={record => record.mapped_Time ? formatLocalTime(record.mapped_Time) : null} />
                    <NumberField source="score" />
                    <NumberField source="listingCount" label="Volume" />
                    <FunctionField label="Tags" source="tags" render={record => {
                        if (!record.tags) return null;
                        const tags = record.tags.split(',').filter((t: string) => t !== '');
                        return (
                            <Tooltip  title={`Tagged by: ${record.updatedBy || 'N/A'}`}>
                                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    {tags.map((tag: string, index: number) => <Chip key={index} label={tag} size="small" sx={{ height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal', wordWrap: 'break-word', padding: '4px 8px' } }}/>)}
                                </span>
                            </Tooltip>
                        );
                    }} />
                </DatagridConfigurable>
            </RaList>

            <MappingPopup dataProvider={dataProvider} data={state} smMarket={selection.MarketId} handleClose={handleCloseDialog} reloadPage={() => reloadPage(resource, refbtnFilter)} />
            <SyncEventPopup dataProvider={dataProvider} data={state} handleClose={handleCloseDialog} />
            <Backdrop
                sx={(theme) => ({
                    color: '#fff',
                    zIndex: theme.zIndex.drawer + 1 // Make sure the backdrop is above other elements
                })}
                open={state.openBackdrop}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

const ListingsMappingPage = {
    name: 'ListingsMapping',
    list: ListingsMapping
};
export default ListingsMappingPage;
